import { missingCalculations } from "@/utils";
import { Federation } from "./Federation";

export interface CalculationBase {
  nhoNumber: number;
  childNhoNumber: number;
  childPriorityClass: number;
  triggeredByMembershipNhoNumber?: number | null;
  customCalculationText: string;
  year: number;
  isActive: boolean;
  organizationNumber: number;
}

export interface Calculation extends CalculationBase {
  blocks: Array<CalculationBlock>;
  calculationType: string;
  membershipType: number;
  hasUnsavedChanges?: boolean;
  childOrganizationCalculations: Array<Calculation>;
}

export interface CalculationDto extends CalculationBase {
  blocks: string;
  calculationType: number;
  membershipType: number;
  childOrganizationCalculations: Array<CalculationDto>;
}

export interface CalculationBlock {
  type: string;
  displayName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  blockId: number;
  isDirty: boolean;
  isExpanded: boolean;
}

const calculationTypes = ["Contingent", "ServiceFee"];

const checkEquality = (
  calc: Calculation,
  year: number,
  nhoNumber: number,
  calculationType: string,
  membershipType: number
): boolean =>
  calc.nhoNumber === nhoNumber &&
  calc.year === year &&
  calc.calculationType === calculationType &&
  calc.membershipType === membershipType;

const checkExistance = (
  calculations: Calculation[],
  year: number,
  org: Federation
): [boolean, boolean] => {
  const contingentExistance = calculations.some((calc) =>
    checkEquality(calc, year, org.nhoNumber, "Contingent", 0)
  );
  const serviceFeeExistance = calculations.some((calc) =>
    checkEquality(calc, year, org.nhoNumber, "ServiceFee", 0)
  );

  return [contingentExistance, serviceFeeExistance];
};

const createUniqueItem = (
  year: number,
  nhoNumber: number,
  calculationType: string,
  organizationNumber: number
): Calculation => ({
  year,
  childNhoNumber: 0,
  nhoNumber,
  organizationNumber,
  calculationType,
  customCalculationText: "",
  childPriorityClass: 0,
  triggeredByMembershipNhoNumber: null,
  isActive: true,
  hasUnsavedChanges: false,
  membershipType: 0,
  blocks: [],
  childOrganizationCalculations: [],
});

const hasChildCalculation = (calculations: Calculation[], childMo: number) => {
  return calculations.some((calculation) => {
    return calculation.childNhoNumber === childMo;
  });
};

export const CalculationUtils = {
  checkExistance,
  createUniqueItem,
  hasChildCalculation,
};

export function filterCalculations(
  calculations: Calculation[],
  filter: {
    name: string;
    types: string[];
    membershipTypes: number[];
    year: string;
    showDeactivated: boolean;
    showOnlyMissingRows: boolean;
  },
  associations: Federation[]
): Calculation[] {
  let filteredCalculations = calculations;
  const {
    name,
    types,
    membershipTypes,
    year,
    showDeactivated,
    showOnlyMissingRows,
  } = filter;

  //Show only missing rows - this disabled all other filters
  if (showOnlyMissingRows) {
    filteredCalculations = missingCalculations(
      calculations,
      associations,
      parseInt(year, 10)
    );
    return filteredCalculations;
  }

  //FILTER NAME
  if (name !== "") {
    filteredCalculations = filteredCalculations.filter(
      (calculation: Calculation) => {
        const fName = name.toLowerCase().trim();
        const federationName =
          associations.find(
            (a: Federation) => a.nhoNumber === calculation.nhoNumber
          )?.name ?? calculation.nhoNumber.toString();
        const fCalcName = federationName.toLowerCase().trim();
        const fCalcNumber = calculation.nhoNumber?.toString() ?? "";

        return fCalcName.includes(fName) || fCalcNumber.includes(fName);
      }
    );
  }

  //FILTER CALCULATION TYPES
  if (types.length > 0) {
    filteredCalculations = filteredCalculations.filter(
      (calculation: Calculation) => {
        return types.includes(calculation.calculationType);
      }
    );
  }

  //FILTER MEMBERSHIP TYPES
  if (membershipTypes.length > 0) {
    filteredCalculations = filteredCalculations.filter(
      (calculation: Calculation) => {
        return membershipTypes.includes(calculation.membershipType);
      }
    );
  }

  //FILTER YEAR
  if (year !== "") {
    filteredCalculations = filteredCalculations.filter(
      (calculation: Calculation) => {
        return calculation.year.toString().includes(year);
      }
    );
  }

  //Filter deactivated calculations
  if (!showDeactivated) {
    filteredCalculations = filteredCalculations.filter(
      (calculation: Calculation) => calculation.isActive
    );
  }

  return filteredCalculations;
}

export function prepareForServer(calculation: Calculation): CalculationDto {
  const blocks = calculation.blocks.map((x, index) => {
    const blockData = x.data;
    blockData.blockId = index;
    return blockData;
  });

  return {
    blocks: JSON.stringify(blocks),
    calculationType: calculationTypes.indexOf(calculation.calculationType),
    nhoNumber: calculation.nhoNumber,
    organizationNumber: calculation.organizationNumber,
    childNhoNumber: calculation.childNhoNumber,
    childPriorityClass: calculation.childPriorityClass,
    triggeredByMembershipNhoNumber:
      calculation.triggeredByMembershipNhoNumber ?? null,
    customCalculationText: calculation.customCalculationText,
    year: calculation.year,
    membershipType: calculation.membershipType,
    isActive: calculation.isActive,
    childOrganizationCalculations: [], //only the submitted calculation will be updated
  };
}

export function convertFromServer(calculationDto: CalculationDto): Calculation {
  let blockIndex = 0;
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    blocks: JSON.parse(calculationDto.blocks).map((x: any) => {
      return {
        data: x,
        type: x.type,
        blockId: blockIndex++,
        isExpanded: false,
      } as CalculationBlock;
    }),
    calculationType: calculationTypes[calculationDto.calculationType],
    year: calculationDto.year,
    nhoNumber: calculationDto.nhoNumber,
    customCalculationText: calculationDto.customCalculationText,
    organizationNumber: calculationDto.organizationNumber,
    childNhoNumber: calculationDto.childNhoNumber,
    childPriorityClass: calculationDto.childPriorityClass,
    triggeredByMembershipNhoNumber:
      calculationDto.triggeredByMembershipNhoNumber ?? null,
    hasUnsavedChanges: false,
    isActive: calculationDto.isActive,
    membershipType: calculationDto.membershipType,
    childOrganizationCalculations:
      calculationDto.childOrganizationCalculations?.map(convertFromServer) ??
      [],
  };
}
